/**
 * PayrollTab.js - Enhanced Payroll Management with Preview/Review
 * Handles pay stubs, payroll runs with preview, adjustments, and payment processing
 */
import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  DollarSign, Calculator, Users, Clock, Check, X, 
  Eye, FileText, Plus, Calendar, AlertTriangle, Send,
  ChevronDown, ChevronUp, Trash2, Settings, Download,
  CreditCard, Banknote, Building, Gift, MinusCircle,
  RefreshCw, Search, Filter, MoreVertical
} from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { toast } from 'sonner';
import api from '../../services/api';

const PayrollTab = () => {
  // Data state
  const [payStubs, setPayStubs] = useState([]);
  const [workers, setWorkers] = useState([]);
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  
  // Pay period state
  const [payPeriodStart, setPayPeriodStart] = useState('');
  const [payPeriodEnd, setPayPeriodEnd] = useState('');
  
  // Preview state
  const [showPreview, setShowPreview] = useState(false);
  const [previewData, setPreviewData] = useState(null);
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [runningPayroll, setRunningPayroll] = useState(false);
  
  // Settings modal
  const [showSettings, setShowSettings] = useState(false);
  const [editSettings, setEditSettings] = useState({});
  
  // Calculator/Manual stub state
  const [showCalculator, setShowCalculator] = useState(false);
  const [selectedWorker, setSelectedWorker] = useState(null);
  const [calcHours, setCalcHours] = useState('');
  const [calcRate, setCalcRate] = useState('');
  const [calcOvertime, setCalcOvertime] = useState('');
  const [calcBonus, setCalcBonus] = useState('');
  const [calcResult, setCalcResult] = useState(null);
  
  // Adjustment modal
  const [showAdjustment, setShowAdjustment] = useState(false);
  const [adjustmentType, setAdjustmentType] = useState('bonus');
  const [adjustmentWorker, setAdjustmentWorker] = useState('');
  const [adjustmentCategory, setAdjustmentCategory] = useState('');
  const [adjustmentDesc, setAdjustmentDesc] = useState('');
  const [adjustmentAmount, setAdjustmentAmount] = useState('');
  
  // Pay stub actions
  const [processingId, setProcessingId] = useState(null);
  const [expandedStub, setExpandedStub] = useState(null);
  
  // Payment modal
  const [showPayModal, setShowPayModal] = useState(false);
  const [payingStub, setPayingStub] = useState(null);
  const [paymentMethod, setPaymentMethod] = useState('direct_deposit');
  const [paymentReference, setPaymentReference] = useState('');
  const [paymentNotes, setPaymentNotes] = useState('');
  
  // View stub detail
  const [viewingStub, setViewingStub] = useState(null);

  // W-2 / Tax Forms state
  const [showW2Modal, setShowW2Modal] = useState(false);
  const [w2Year, setW2Year] = useState(new Date().getFullYear() - 1);
  const [w2Workers, setW2Workers] = useState([]);
  const [generatingW2, setGeneratingW2] = useState(null);
  const [w2Generated, setW2Generated] = useState([]);

  // Fetch pay stubs
  const fetchPayStubs = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/payroll/stubs');
      setPayStubs(res.data || []);
    } catch (error) {
      console.error('Failed to fetch pay stubs:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  // Fetch workers
  const fetchWorkers = useCallback(async () => {
    try {
      const res = await api.get('/workers');
      setWorkers(res.data || []);
    } catch (error) {
      console.error('Failed to fetch workers:', error);
    }
  }, []);

  // Fetch settings
  const fetchSettings = useCallback(async () => {
    try {
      const res = await api.get('/payroll/settings');
      setSettings(res.data);
      setEditSettings(res.data);
    } catch (error) {
      console.error('Failed to fetch settings:', error);
    }
  }, []);

  useEffect(() => {
    fetchPayStubs();
    fetchWorkers();
    fetchSettings();
    
    // Set default pay period dates (last 2 weeks)
    const today = new Date();
    const twoWeeksAgo = new Date(today);
    twoWeeksAgo.setDate(twoWeeksAgo.getDate() - 14);
    setPayPeriodStart(twoWeeksAgo.toISOString().split('T')[0]);
    setPayPeriodEnd(today.toISOString().split('T')[0]);
  }, [fetchPayStubs, fetchWorkers, fetchSettings]);

  // Preview payroll
  const previewPayroll = async () => {
    if (!payPeriodStart || !payPeriodEnd) {
      toast.error('Please select pay period dates');
      return;
    }
    
    setLoadingPreview(true);
    try {
      const res = await api.get('/payroll/preview', {
        params: { pay_period_start: payPeriodStart, pay_period_end: payPeriodEnd }
      });
      setPreviewData(res.data);
      setShowPreview(true);
    } catch (error) {
      toast.error('Failed to preview payroll');
    } finally {
      setLoadingPreview(false);
    }
  };

  // Run payroll (after preview)
  const runPayroll = async () => {
    setRunningPayroll(true);
    try {
      const res = await api.post('/payroll/run-enhanced', null, {
        params: { pay_period_start: payPeriodStart, pay_period_end: payPeriodEnd }
      });
      
      toast.success(`Created ${res.data.pay_stubs?.length || 0} pay stubs. Total: $${res.data.summary?.total_net_pay?.toFixed(2)}`);
      setShowPreview(false);
      setPreviewData(null);
      fetchPayStubs();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to run payroll');
    } finally {
      setRunningPayroll(false);
    }
  };

  // Save settings
  const saveSettings = async () => {
    try {
      await api.put('/payroll/settings', editSettings);
      toast.success('Settings saved');
      setShowSettings(false);
      fetchSettings();
    } catch (error) {
      toast.error('Failed to save settings');
    }
  };

  // Add adjustment
  const addAdjustment = async () => {
    if (!adjustmentWorker || !adjustmentDesc || !adjustmentAmount) {
      toast.error('Please fill all fields');
      return;
    }
    
    try {
      const endpoint = `/payroll/adjustments/${adjustmentType}`;
      await api.post(endpoint, {
        worker_id: adjustmentWorker,
        pay_period_start: payPeriodStart,
        pay_period_end: payPeriodEnd,
        [`${adjustmentType}_type`]: adjustmentCategory || 'other',
        description: adjustmentDesc,
        amount: parseFloat(adjustmentAmount)
      });
      
      toast.success(`${adjustmentType.charAt(0).toUpperCase() + adjustmentType.slice(1)} added`);
      setShowAdjustment(false);
      setAdjustmentWorker('');
      setAdjustmentCategory('');
      setAdjustmentDesc('');
      setAdjustmentAmount('');
      
      // Refresh preview if open
      if (showPreview) {
        previewPayroll();
      }
    } catch (error) {
      toast.error(`Failed to add ${adjustmentType}`);
    }
  };

  // Calculate pay (manual)
  const calculatePay = () => {
    const hours = parseFloat(calcHours) || 0;
    const rate = parseFloat(calcRate) || 0;
    const overtime = parseFloat(calcOvertime) || 0;
    const bonus = parseFloat(calcBonus) || 0;
    const overtimeMult = settings?.overtime_multiplier || 1.5;
    
    const regularPay = hours * rate;
    const overtimePay = overtime * rate * overtimeMult;
    const grossPay = regularPay + overtimePay + bonus;
    
    setCalcResult({
      regular_pay: regularPay.toFixed(2),
      overtime_pay: overtimePay.toFixed(2),
      gross_pay: grossPay.toFixed(2),
      net_pay: grossPay.toFixed(2),
      hours,
      overtime_hours: overtime
    });
  };

  // Save manual pay stub
  const saveManualPayStub = async () => {
    if (!selectedWorker || !calcResult) {
      toast.error('Please select a worker and calculate pay first');
      return;
    }
    
    setProcessingId('manual');
    try {
      await api.post('/payroll/manual-stub', {
        worker_id: selectedWorker.id,
        worker_name: selectedWorker.full_name,
        hours_worked: parseFloat(calcHours) || 0,
        overtime_hours: parseFloat(calcOvertime) || 0,
        hourly_rate: parseFloat(calcRate) || 0,
        bonus: parseFloat(calcBonus) || 0,
        pay_period_start: payPeriodStart,
        pay_period_end: payPeriodEnd
      });
      
      toast.success('Pay stub created');
      setShowCalculator(false);
      setCalcResult(null);
      setSelectedWorker(null);
      setCalcHours('');
      setCalcRate('');
      setCalcOvertime('');
      setCalcBonus('');
      fetchPayStubs();
    } catch (error) {
      toast.error('Failed to create pay stub');
    } finally {
      setProcessingId(null);
    }
  };

  // Approve pay stub
  const approvePayStub = async (stubId) => {
    setProcessingId(stubId);
    try {
      await api.post(`/payroll/stubs/${stubId}/approve`);
      toast.success('Pay stub approved');
      fetchPayStubs();
    } catch (error) {
      toast.error('Failed to approve pay stub');
    } finally {
      setProcessingId(null);
    }
  };

  // Process payment
  const processPayment = async () => {
    if (!payingStub) return;
    
    setProcessingId(payingStub.id);
    try {
      await api.post(`/payroll/stubs/${payingStub.id}/pay`, null, {
        params: { 
          payment_method: paymentMethod,
          payment_reference: paymentReference || undefined,
          notes: paymentNotes || undefined
        }
      });
      
      toast.success(`Paid $${payingStub.net_pay?.toFixed(2)} to ${payingStub.worker_name}`);
      setShowPayModal(false);
      setPayingStub(null);
      setPaymentMethod('direct_deposit');
      setPaymentReference('');
      setPaymentNotes('');
      fetchPayStubs();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to process payment');
    } finally {
      setProcessingId(null);
    }
  };

  // Delete pay stub
  const deletePayStub = async (stubId) => {
    if (!window.confirm('Delete this pay stub?')) return;
    
    setProcessingId(stubId);
    try {
      await api.delete(`/payroll/stubs/${stubId}`);
      toast.success('Pay stub deleted');
      fetchPayStubs();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete pay stub');
    } finally {
      setProcessingId(null);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'pending': case 'draft': return 'bg-amber-100 text-amber-800';
      case 'approved': return 'bg-blue-100 text-blue-800';
      case 'paid': return 'bg-green-100 text-green-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getPaymentMethodIcon = (method) => {
    switch (method) {
      case 'direct_deposit': return <Building className="w-4 h-4" />;
      case 'check': return <FileText className="w-4 h-4" />;
      case 'cash': return <Banknote className="w-4 h-4" />;
      case 'venmo': case 'zelle': return <CreditCard className="w-4 h-4" />;
      default: return <DollarSign className="w-4 h-4" />;
    }
  };

  // W-2 Generation Functions
  const openW2Modal = async () => {
    setShowW2Modal(true);
    try {
      // Fetch workers with payroll data for the selected year
      const workersRes = await api.get('/workers');
      setW2Workers(workersRes.data || []);
      
      // Check which W-2s have already been generated
      const w2Res = await api.get('/owner/tax-forms/w4');
      const existingW2s = w2Res.data?.filter(w => w.tax_year === w2Year) || [];
      setW2Generated(existingW2s.map(w => w.worker_id));
    } catch (error) {
      console.error('Failed to load W-2 data:', error);
    }
  };

  const generateW2 = async (workerId, workerName) => {
    setGeneratingW2(workerId);
    try {
      await api.post(`/owner/tax-forms/w2/generate/${workerId}/${w2Year}`);
      toast.success(`W-2 generated for ${workerName}`);
      setW2Generated(prev => [...prev, workerId]);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to generate W-2');
    } finally {
      setGeneratingW2(null);
    }
  };

  const generateAllW2s = async () => {
    const workersToGenerate = w2Workers.filter(w => !w2Generated.includes(w.id));
    for (const worker of workersToGenerate) {
      await generateW2(worker.id, worker.full_name);
    }
    toast.success(`Generated ${workersToGenerate.length} W-2 forms`);
  };

  // Group pay stubs by status
  const pendingStubs = payStubs.filter(s => s.status === 'pending' || s.status === 'draft');
  const approvedStubs = payStubs.filter(s => s.status === 'approved');
  const paidStubs = payStubs.filter(s => s.status === 'paid');

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} data-testid="payroll-tab">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
        <h1 className="text-2xl lg:text-3xl font-bold font-['Cormorant_Garamond']">Payroll Management</h1>
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" size="sm" onClick={() => setShowSettings(true)} data-testid="payroll-settings-btn">
            <Settings className="w-4 h-4 mr-2" />
            Settings
          </Button>
          <Button variant="outline" size="sm" onClick={openW2Modal} data-testid="w2-generation-btn">
            <FileText className="w-4 h-4 mr-2" />
            W-2 Forms
          </Button>
          <Button variant="outline" size="sm" onClick={() => setShowCalculator(true)} data-testid="calculator-btn">
            <Calculator className="w-4 h-4 mr-2" />
            Calculator
          </Button>
          <Button onClick={previewPayroll} disabled={loadingPreview} className="btn-primary" data-testid="preview-payroll-btn">
            <Eye className="w-4 h-4 mr-2" />
            {loadingPreview ? 'Loading...' : 'Preview Payroll'}
          </Button>
        </div>
      </div>

      {/* Pay Period Selection */}
      <div className="card p-4 mb-6">
        <h3 className="font-semibold mb-3 flex items-center gap-2">
          <Calendar className="w-5 h-5 text-[#D4AF37]" />
          Pay Period
        </h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <div>
            <label className="block text-sm font-medium mb-1">Start Date</label>
            <Input
              type="date"
              value={payPeriodStart}
              onChange={(e) => setPayPeriodStart(e.target.value)}
              data-testid="pay-period-start"
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">End Date</label>
            <Input
              type="date"
              value={payPeriodEnd}
              onChange={(e) => setPayPeriodEnd(e.target.value)}
              data-testid="pay-period-end"
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Period Type</label>
            <p className="text-lg font-semibold capitalize">{settings?.pay_period_type || 'Biweekly'}</p>
          </div>
          <div className="flex items-end gap-2">
            <Button variant="outline" onClick={() => setShowAdjustment(true)} data-testid="add-adjustment-btn">
              <Plus className="w-4 h-4 mr-1" />
              Add Adjustment
            </Button>
          </div>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <div className="card p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-amber-100 rounded-lg">
              <Clock className="w-5 h-5 text-amber-600" />
            </div>
            <div>
              <p className="text-2xl font-bold">{pendingStubs.length}</p>
              <p className="text-xs text-[var(--text-muted)]">Pending</p>
            </div>
          </div>
        </div>
        <div className="card p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-blue-100 rounded-lg">
              <Check className="w-5 h-5 text-blue-600" />
            </div>
            <div>
              <p className="text-2xl font-bold">{approvedStubs.length}</p>
              <p className="text-xs text-[var(--text-muted)]">Approved</p>
            </div>
          </div>
        </div>
        <div className="card p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-green-100 rounded-lg">
              <DollarSign className="w-5 h-5 text-green-600" />
            </div>
            <div>
              <p className="text-2xl font-bold">{paidStubs.length}</p>
              <p className="text-xs text-[var(--text-muted)]">Paid</p>
            </div>
          </div>
        </div>
        <div className="card p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-[#D4AF37]/20 rounded-lg">
              <Users className="w-5 h-5 text-[#D4AF37]" />
            </div>
            <div>
              <p className="text-2xl font-bold">${approvedStubs.reduce((sum, s) => sum + (s.net_pay || 0), 0).toFixed(0)}</p>
              <p className="text-xs text-[var(--text-muted)]">To Pay</p>
            </div>
          </div>
        </div>
      </div>

      {/* Pay Stubs List */}
      <div className="card overflow-hidden">
        <div className="p-4 bg-gradient-to-r from-[#D4AF37] to-[#B8962E] text-black">
          <h2 className="text-lg font-semibold flex items-center gap-2">
            <FileText className="w-5 h-5" />
            Pay Stubs ({payStubs.length})
          </h2>
        </div>
        
        {loading ? (
          <div className="p-8 text-center">
            <div className="w-8 h-8 border-4 border-[#D4AF37] border-t-transparent rounded-full animate-spin mx-auto" />
          </div>
        ) : payStubs.length === 0 ? (
          <div className="p-8 text-center text-[var(--text-muted)]">
            <FileText className="w-12 h-12 mx-auto mb-3 opacity-30" />
            <p>No pay stubs yet</p>
            <p className="text-sm mt-1">Preview and run payroll to create pay stubs</p>
          </div>
        ) : (
          <div className="divide-y divide-[var(--border-subtle)]">
            {payStubs.slice(0, 20).map(stub => (
              <div 
                key={stub.id} 
                className="p-4 hover:bg-[var(--background-subtle)] transition-colors"
                data-testid={`paystub-${stub.id}`}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div className="w-10 h-10 rounded-full bg-[#D4AF37]/20 flex items-center justify-center">
                      <span className="text-[#D4AF37] font-bold text-sm">
                        {stub.worker_name?.split(' ').map(n => n[0]).join('') || '?'}
                      </span>
                    </div>
                    <div>
                      <p className="font-semibold">{stub.worker_name}</p>
                      <p className="text-sm text-[var(--text-muted)]">
                        {stub.pay_period_start} to {stub.pay_period_end}
                      </p>
                      <div className="flex items-center gap-2 mt-1">
                        <span className={`px-2 py-0.5 rounded-full text-xs ${getStatusColor(stub.status)}`}>
                          {stub.status}
                        </span>
                        {stub.is_manual && (
                          <span className="px-2 py-0.5 bg-purple-100 text-purple-700 rounded-full text-xs">Manual</span>
                        )}
                        {stub.overtime_hours > 0 && (
                          <span className="px-2 py-0.5 bg-orange-100 text-orange-700 rounded-full text-xs">
                            +{stub.overtime_hours}h OT
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-4">
                    <div className="text-right">
                      <p className="text-xl font-bold text-[#D4AF37]">${stub.net_pay?.toFixed(2)}</p>
                      <p className="text-sm text-[var(--text-muted)]">
                        {stub.total_hours?.toFixed(1)}h @ ${stub.hourly_rate || stub.base_rate}/hr
                      </p>
                    </div>
                    
                    <div className="flex items-center gap-1">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setViewingStub(stub)}
                        data-testid={`view-stub-${stub.id}`}
                      >
                        <Eye className="w-4 h-4" />
                      </Button>
                      
                      {(stub.status === 'pending' || stub.status === 'draft') && (
                        <>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => approvePayStub(stub.id)}
                            disabled={processingId === stub.id}
                            className="text-blue-600"
                            data-testid={`approve-stub-${stub.id}`}
                          >
                            <Check className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => deletePayStub(stub.id)}
                            disabled={processingId === stub.id}
                            className="text-red-600"
                            data-testid={`delete-stub-${stub.id}`}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </>
                      )}
                      
                      {stub.status === 'approved' && (
                        <Button
                          size="sm"
                          onClick={() => { setPayingStub(stub); setShowPayModal(true); }}
                          disabled={processingId === stub.id}
                          className="bg-green-600 hover:bg-green-700 text-white"
                          data-testid={`pay-stub-${stub.id}`}
                        >
                          <Send className="w-4 h-4 mr-1" />
                          Pay
                        </Button>
                      )}
                      
                      {stub.status === 'paid' && (
                        <span className="text-green-600 text-sm flex items-center gap-1">
                          {getPaymentMethodIcon(stub.payment_method)}
                          Paid
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Preview Modal */}
      <AnimatePresence>
        {showPreview && previewData && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4 overflow-y-auto">
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              className="bg-[var(--background-paper)] rounded-xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden"
            >
              <div className="p-4 bg-gradient-to-r from-[#D4AF37] to-[#B8962E] text-black flex justify-between items-center">
                <div>
                  <h2 className="text-xl font-bold">Payroll Preview</h2>
                  <p className="text-sm opacity-80">
                    {previewData.pay_period_start} to {previewData.pay_period_end}
                  </p>
                </div>
                <Button variant="ghost" onClick={() => setShowPreview(false)}>
                  <X className="w-5 h-5" />
                </Button>
              </div>
              
              <div className="p-4 max-h-[60vh] overflow-y-auto">
                {/* Summary */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
                  <div className="p-3 bg-[var(--background-subtle)] rounded-lg text-center">
                    <p className="text-2xl font-bold">{previewData.summary?.total_workers || 0}</p>
                    <p className="text-xs text-[var(--text-muted)]">Workers</p>
                  </div>
                  <div className="p-3 bg-[var(--background-subtle)] rounded-lg text-center">
                    <p className="text-2xl font-bold">{previewData.summary?.total_hours?.toFixed(1) || 0}</p>
                    <p className="text-xs text-[var(--text-muted)]">Total Hours</p>
                  </div>
                  <div className="p-3 bg-[var(--background-subtle)] rounded-lg text-center">
                    <p className="text-2xl font-bold">${previewData.summary?.total_gross_pay?.toFixed(0) || 0}</p>
                    <p className="text-xs text-[var(--text-muted)]">Gross Pay</p>
                  </div>
                  <div className="p-3 bg-green-100 rounded-lg text-center">
                    <p className="text-2xl font-bold text-green-700">${previewData.summary?.total_net_pay?.toFixed(0) || 0}</p>
                    <p className="text-xs text-green-600">Net Pay</p>
                  </div>
                </div>
                
                {/* Warnings */}
                {previewData.warnings?.length > 0 && (
                  <div className="mb-4 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                    <p className="font-semibold text-amber-800 flex items-center gap-2 mb-2">
                      <AlertTriangle className="w-4 h-4" />
                      Warnings
                    </p>
                    <ul className="text-sm text-amber-700 list-disc list-inside">
                      {previewData.warnings.map((w, i) => <li key={`warning-${i}-${w.substring(0,20)}`}>{w}</li>)}
                    </ul>
                  </div>
                )}
                
                {/* Workers List */}
                <div className="space-y-3">
                  {previewData.workers?.map(worker => (
                    <div key={worker.worker_id} className="border rounded-lg p-4">
                      <div className="flex justify-between items-start mb-3">
                        <div>
                          <p className="font-semibold">{worker.worker_name}</p>
                          <p className="text-sm text-[var(--text-muted)]">
                            {worker.jobs_count} jobs • {worker.total_hours?.toFixed(1)}h 
                            {worker.overtime_hours > 0 && ` (+${worker.overtime_hours?.toFixed(1)}h OT)`}
                          </p>
                        </div>
                        <div className="text-right">
                          <p className="text-xl font-bold text-[#D4AF37]">${worker.net_pay?.toFixed(2)}</p>
                          <p className="text-sm text-[var(--text-muted)]">@ ${worker.base_rate}/hr</p>
                        </div>
                      </div>
                      
                      {/* Pay Breakdown */}
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-sm">
                        <div className="p-2 bg-[var(--background-subtle)] rounded">
                          <p className="text-[var(--text-muted)]">Regular</p>
                          <p className="font-semibold">${worker.regular_pay?.toFixed(2)}</p>
                        </div>
                        {worker.overtime_pay > 0 && (
                          <div className="p-2 bg-orange-50 rounded">
                            <p className="text-orange-600">Overtime</p>
                            <p className="font-semibold text-orange-700">${worker.overtime_pay?.toFixed(2)}</p>
                          </div>
                        )}
                        {worker.total_bonuses > 0 && (
                          <div className="p-2 bg-green-50 rounded">
                            <p className="text-green-600">Bonuses</p>
                            <p className="font-semibold text-green-700">+${worker.total_bonuses?.toFixed(2)}</p>
                          </div>
                        )}
                        {worker.total_deductions > 0 && (
                          <div className="p-2 bg-red-50 rounded">
                            <p className="text-red-600">Deductions</p>
                            <p className="font-semibold text-red-700">-${worker.total_deductions?.toFixed(2)}</p>
                          </div>
                        )}
                      </div>
                      
                      {/* Status flags */}
                      {(worker.pending_approval || worker.self_signed) && (
                        <div className="mt-2 flex gap-2">
                          {worker.pending_approval && (
                            <span className="text-xs px-2 py-1 bg-amber-100 text-amber-700 rounded">
                              Pending Approval
                            </span>
                          )}
                          {worker.self_signed && (
                            <span className="text-xs px-2 py-1 bg-purple-100 text-purple-700 rounded">
                              Self-Signed Records
                            </span>
                          )}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
              
              <div className="p-4 border-t flex justify-between items-center">
                <p className="text-sm text-[var(--text-muted)]">
                  Review carefully before running payroll
                </p>
                <div className="flex gap-2">
                  <Button variant="outline" onClick={() => setShowPreview(false)}>Cancel</Button>
                  <Button 
                    onClick={runPayroll} 
                    disabled={runningPayroll || !previewData.can_run}
                    className="bg-green-600 hover:bg-green-700 text-white"
                    data-testid="run-payroll-btn"
                  >
                    {runningPayroll ? 'Processing...' : `Run Payroll ($${previewData.summary?.total_net_pay?.toFixed(2)})`}
                  </Button>
                </div>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* Settings Modal */}
      <AnimatePresence>
        {showSettings && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              className="bg-[var(--background-paper)] rounded-xl shadow-2xl max-w-md w-full"
            >
              <div className="p-4 border-b flex justify-between items-center">
                <h2 className="text-xl font-bold">Payroll Settings</h2>
                <Button variant="ghost" size="sm" onClick={() => setShowSettings(false)}>
                  <X className="w-5 h-5" />
                </Button>
              </div>
              <div className="p-4 space-y-4">
                <div>
                  <label className="block text-sm font-medium mb-2">Pay Period Type</label>
                  <select
                    className="w-full p-2 border border-[var(--border-primary)] rounded-lg bg-[var(--background-paper)] text-[var(--text-primary)]"
                    value={editSettings.pay_period_type || 'biweekly'}
                    onChange={(e) => setEditSettings({...editSettings, pay_period_type: e.target.value})}
                  >
                    <option value="weekly">Weekly</option>
                    <option value="biweekly">Bi-Weekly</option>
                    <option value="monthly">Monthly</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">Default Hourly Rate</label>
                  <Input
                    type="number"
                    value={editSettings.default_hourly_rate || 25}
                    onChange={(e) => setEditSettings({...editSettings, default_hourly_rate: parseFloat(e.target.value)})}
                  />
                </div>
                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    id="overtime"
                    checked={editSettings.overtime_enabled !== false}
                    onChange={(e) => setEditSettings({...editSettings, overtime_enabled: e.target.checked})}
                    className="w-4 h-4"
                  />
                  <label htmlFor="overtime" className="text-sm font-medium">Enable Overtime</label>
                </div>
                {editSettings.overtime_enabled !== false && (
                  <>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium mb-2">Weekly Threshold</label>
                        <Input
                          type="number"
                          value={editSettings.overtime_threshold_weekly || 40}
                          onChange={(e) => setEditSettings({...editSettings, overtime_threshold_weekly: parseFloat(e.target.value)})}
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium mb-2">OT Multiplier</label>
                        <Input
                          type="number"
                          step="0.1"
                          value={editSettings.overtime_multiplier || 1.5}
                          onChange={(e) => setEditSettings({...editSettings, overtime_multiplier: parseFloat(e.target.value)})}
                        />
                      </div>
                    </div>
                  </>
                )}
              </div>
              <div className="p-4 border-t flex justify-end gap-2">
                <Button variant="outline" onClick={() => setShowSettings(false)}>Cancel</Button>
                <Button onClick={saveSettings}>Save Settings</Button>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* Adjustment Modal */}
      <AnimatePresence>
        {showAdjustment && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              className="bg-[var(--background-paper)] rounded-xl shadow-2xl max-w-md w-full"
            >
              <div className="p-4 border-b">
                <h2 className="text-xl font-bold">Add Adjustment</h2>
              </div>
              <div className="p-4 space-y-4">
                <div className="flex gap-2">
                  {['bonus', 'deduction', 'reimbursement'].map(type => (
                    <Button
                      key={type}
                      variant={adjustmentType === type ? 'default' : 'outline'}
                      size="sm"
                      onClick={() => setAdjustmentType(type)}
                      className={adjustmentType === type ? 'bg-[#D4AF37] text-black' : ''}
                    >
                      {type === 'bonus' && <Gift className="w-4 h-4 mr-1" />}
                      {type === 'deduction' && <MinusCircle className="w-4 h-4 mr-1" />}
                      {type === 'reimbursement' && <RefreshCw className="w-4 h-4 mr-1" />}
                      {type.charAt(0).toUpperCase() + type.slice(1)}
                    </Button>
                  ))}
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">Worker</label>
                  <select
                    className="w-full p-2 border border-[var(--border-primary)] rounded-lg bg-[var(--background-paper)] text-[var(--text-primary)]"
                    value={adjustmentWorker}
                    onChange={(e) => setAdjustmentWorker(e.target.value)}
                  >
                    <option value="">Select worker...</option>
                    {workers.map(w => (
                      <option key={w.id} value={w.id}>{w.full_name}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">Category</label>
                  <select
                    className="w-full p-2 border border-[var(--border-primary)] rounded-lg bg-[var(--background-paper)] text-[var(--text-primary)]"
                    value={adjustmentCategory}
                    onChange={(e) => setAdjustmentCategory(e.target.value)}
                  >
                    {adjustmentType === 'bonus' && (
                      <>
                        <option value="tip">Tip</option>
                        <option value="performance">Performance</option>
                        <option value="referral">Referral</option>
                        <option value="other">Other</option>
                      </>
                    )}
                    {adjustmentType === 'deduction' && (
                      <>
                        <option value="advance">Advance</option>
                        <option value="equipment">Equipment</option>
                        <option value="uniform">Uniform</option>
                        <option value="other">Other</option>
                      </>
                    )}
                    {adjustmentType === 'reimbursement' && (
                      <>
                        <option value="mileage">Mileage</option>
                        <option value="supplies">Supplies</option>
                        <option value="other">Other</option>
                      </>
                    )}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">Description</label>
                  <Input
                    placeholder="Enter description..."
                    value={adjustmentDesc}
                    onChange={(e) => setAdjustmentDesc(e.target.value)}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">Amount ($)</label>
                  <Input
                    type="number"
                    step="0.01"
                    placeholder="0.00"
                    value={adjustmentAmount}
                    onChange={(e) => setAdjustmentAmount(e.target.value)}
                  />
                </div>
              </div>
              <div className="p-4 border-t flex justify-end gap-2">
                <Button variant="outline" onClick={() => setShowAdjustment(false)}>Cancel</Button>
                <Button onClick={addAdjustment}>Add {adjustmentType}</Button>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* Calculator Modal */}
      <AnimatePresence>
        {showCalculator && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              className="bg-[var(--background-paper)] rounded-xl shadow-2xl max-w-lg w-full"
            >
              <div className="p-4 border-b">
                <h2 className="text-xl font-bold">Manual Pay Stub</h2>
                <p className="text-sm text-[var(--text-muted)]">Create a pay stub manually</p>
              </div>
              <div className="p-4 space-y-4">
                <div>
                  <label className="block text-sm font-medium mb-2">Worker</label>
                  <select
                    className="w-full p-2 border border-[var(--border-primary)] rounded-lg bg-[var(--background-paper)] text-[var(--text-primary)]"
                    value={selectedWorker?.id || ''}
                    onChange={(e) => {
                      const worker = workers.find(w => w.id === e.target.value);
                      setSelectedWorker(worker);
                      if (worker?.hourly_rate) setCalcRate(worker.hourly_rate.toString());
                    }}
                  >
                    <option value="">Select worker...</option>
                    {workers.map(w => (
                      <option key={w.id} value={w.id}>{w.full_name} (${w.hourly_rate || 25}/hr)</option>
                    ))}
                  </select>
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium mb-2">Regular Hours</label>
                    <Input
                      type="number"
                      placeholder="40"
                      value={calcHours}
                      onChange={(e) => setCalcHours(e.target.value)}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-2">Hourly Rate ($)</label>
                    <Input
                      type="number"
                      placeholder="25"
                      value={calcRate}
                      onChange={(e) => setCalcRate(e.target.value)}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-2">Overtime Hours</label>
                    <Input
                      type="number"
                      placeholder="0"
                      value={calcOvertime}
                      onChange={(e) => setCalcOvertime(e.target.value)}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-2">Bonus ($)</label>
                    <Input
                      type="number"
                      placeholder="0"
                      value={calcBonus}
                      onChange={(e) => setCalcBonus(e.target.value)}
                    />
                  </div>
                </div>
                
                <Button onClick={calculatePay} variant="outline" className="w-full">
                  <Calculator className="w-4 h-4 mr-2" />
                  Calculate
                </Button>
                
                {calcResult && (
                  <div className="p-4 bg-green-50 rounded-lg space-y-2">
                    <div className="flex justify-between">
                      <span>Regular Pay:</span>
                      <span className="font-medium">${calcResult.regular_pay}</span>
                    </div>
                    {parseFloat(calcResult.overtime_pay) > 0 && (
                      <div className="flex justify-between">
                        <span>Overtime Pay ({settings?.overtime_multiplier || 1.5}x):</span>
                        <span className="font-medium">${calcResult.overtime_pay}</span>
                      </div>
                    )}
                    <div className="flex justify-between text-lg font-bold border-t pt-2">
                      <span>Net Pay:</span>
                      <span className="text-green-700">${calcResult.net_pay}</span>
                    </div>
                  </div>
                )}
              </div>
              <div className="p-4 border-t flex justify-end gap-2">
                <Button variant="outline" onClick={() => { setShowCalculator(false); setCalcResult(null); }}>
                  Cancel
                </Button>
                <Button 
                  onClick={saveManualPayStub} 
                  disabled={!calcResult || !selectedWorker || processingId === 'manual'}
                >
                  {processingId === 'manual' ? 'Saving...' : 'Create Pay Stub'}
                </Button>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* Payment Modal */}
      <AnimatePresence>
        {showPayModal && payingStub && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              className="bg-[var(--background-paper)] rounded-xl shadow-2xl max-w-md w-full"
            >
              <div className="p-4 border-b">
                <h2 className="text-xl font-bold">Process Payment</h2>
                <p className="text-sm text-[var(--text-muted)]">
                  Pay ${payingStub.net_pay?.toFixed(2)} to {payingStub.worker_name}
                </p>
              </div>
              <div className="p-4 space-y-4">
                <div>
                  <label className="block text-sm font-medium mb-2">Payment Method</label>
                  <select
                    className="w-full p-2 border border-[var(--border-primary)] rounded-lg bg-[var(--background-paper)] text-[var(--text-primary)]"
                    value={paymentMethod}
                    onChange={(e) => setPaymentMethod(e.target.value)}
                  >
                    <option value="stripe_connect">Stripe Direct Deposit (Auto)</option>
                    <option value="direct_deposit">Manual Direct Deposit</option>
                    <option value="paypal">PayPal</option>
                    <option value="cashapp">Cash App</option>
                    <option value="venmo">Venmo</option>
                    <option value="zelle">Zelle</option>
                    <option value="check">Check</option>
                    <option value="cash">Cash</option>
                  </select>
                  {paymentMethod === 'stripe_connect' && (
                    <p className="text-xs text-green-600 mt-1 flex items-center gap-1">
                      <Check className="w-3 h-3" />
                      Will transfer automatically via Stripe
                    </p>
                  )}
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">Reference # (optional)</label>
                  <Input
                    placeholder="Check #, transaction ID, etc."
                    value={paymentReference}
                    onChange={(e) => setPaymentReference(e.target.value)}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">Notes (optional)</label>
                  <textarea
                    className="w-full p-2 border rounded-lg resize-none bg-[var(--background-paper)]"
                    rows={2}
                    placeholder="Payment notes..."
                    value={paymentNotes}
                    onChange={(e) => setPaymentNotes(e.target.value)}
                  />
                </div>
              </div>
              <div className="p-4 border-t flex justify-end gap-2">
                <Button variant="outline" onClick={() => { setShowPayModal(false); setPayingStub(null); }}>
                  Cancel
                </Button>
                <Button 
                  onClick={processPayment}
                  disabled={processingId === payingStub.id}
                  className="bg-green-600 hover:bg-green-700 text-white"
                >
                  {processingId === payingStub.id ? 'Processing...' : `Pay $${payingStub.net_pay?.toFixed(2)}`}
                </Button>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* View Stub Detail Modal */}
      <AnimatePresence>
        {viewingStub && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4 overflow-y-auto">
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              className="bg-[var(--background-paper)] rounded-xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-hidden"
            >
              <div className="p-4 bg-gradient-to-r from-[#D4AF37] to-[#B8962E] text-black flex justify-between items-center">
                <div>
                  <h2 className="text-xl font-bold">Pay Stub</h2>
                  <p className="text-sm opacity-80">{viewingStub.worker_name}</p>
                </div>
                <Button variant="ghost" onClick={() => setViewingStub(null)}>
                  <X className="w-5 h-5" />
                </Button>
              </div>
              
              <div className="p-4 max-h-[70vh] overflow-y-auto">
                {/* Period & Status */}
                <div className="flex justify-between items-center mb-4 pb-4 border-b">
                  <div>
                    <p className="text-sm text-[var(--text-muted)]">Pay Period</p>
                    <p className="font-semibold">{viewingStub.pay_period_start} to {viewingStub.pay_period_end}</p>
                  </div>
                  <span className={`px-3 py-1 rounded-full text-sm font-medium ${getStatusColor(viewingStub.status)}`}>
                    {viewingStub.status?.toUpperCase()}
                  </span>
                </div>
                
                {/* Hours & Rate */}
                <div className="grid grid-cols-3 gap-4 mb-4">
                  <div className="p-3 bg-[var(--background-subtle)] rounded-lg text-center">
                    <p className="text-2xl font-bold">{viewingStub.total_hours?.toFixed(1)}</p>
                    <p className="text-xs text-[var(--text-muted)]">Total Hours</p>
                  </div>
                  <div className="p-3 bg-[var(--background-subtle)] rounded-lg text-center">
                    <p className="text-2xl font-bold">${viewingStub.hourly_rate || viewingStub.base_rate}</p>
                    <p className="text-xs text-[var(--text-muted)]">Hourly Rate</p>
                  </div>
                  <div className="p-3 bg-[var(--background-subtle)] rounded-lg text-center">
                    <p className="text-2xl font-bold">{viewingStub.jobs_count || 0}</p>
                    <p className="text-xs text-[var(--text-muted)]">Jobs</p>
                  </div>
                </div>
                
                {/* Pay Breakdown */}
                <div className="mb-4">
                  <h3 className="font-semibold mb-2">Pay Breakdown</h3>
                  <div className="space-y-2">
                    <div className="flex justify-between">
                      <span>Regular Pay ({viewingStub.regular_hours?.toFixed(1)}h)</span>
                      <span>${viewingStub.regular_pay?.toFixed(2)}</span>
                    </div>
                    {viewingStub.overtime_hours > 0 && (
                      <div className="flex justify-between text-orange-600">
                        <span>Overtime ({viewingStub.overtime_hours?.toFixed(1)}h × 1.5)</span>
                        <span>${viewingStub.overtime_pay?.toFixed(2)}</span>
                      </div>
                    )}
                    <div className="flex justify-between font-semibold border-t pt-2">
                      <span>Gross Pay</span>
                      <span>${viewingStub.gross_pay?.toFixed(2)}</span>
                    </div>
                  </div>
                </div>
                
                {/* Adjustments */}
                {(viewingStub.total_bonuses > 0 || viewingStub.total_deductions > 0 || viewingStub.total_reimbursements > 0) && (
                  <div className="mb-4">
                    <h3 className="font-semibold mb-2">Adjustments</h3>
                    <div className="space-y-2">
                      {viewingStub.total_bonuses > 0 && (
                        <div className="flex justify-between text-green-600">
                          <span>Bonuses</span>
                          <span>+${viewingStub.total_bonuses?.toFixed(2)}</span>
                        </div>
                      )}
                      {viewingStub.total_deductions > 0 && (
                        <div className="flex justify-between text-red-600">
                          <span>Deductions</span>
                          <span>-${viewingStub.total_deductions?.toFixed(2)}</span>
                        </div>
                      )}
                      {viewingStub.total_reimbursements > 0 && (
                        <div className="flex justify-between text-blue-600">
                          <span>Reimbursements</span>
                          <span>+${viewingStub.total_reimbursements?.toFixed(2)}</span>
                        </div>
                      )}
                    </div>
                  </div>
                )}
                
                {/* Net Pay */}
                <div className="p-4 bg-green-50 rounded-lg mb-4">
                  <div className="flex justify-between items-center">
                    <span className="text-lg font-bold">Net Pay</span>
                    <span className="text-2xl font-bold text-green-700">${viewingStub.net_pay?.toFixed(2)}</span>
                  </div>
                </div>
                
                {/* YTD Totals */}
                {(viewingStub.ytd_gross || viewingStub.ytd_net) && (
                  <div className="mb-4">
                    <h3 className="font-semibold mb-2">Year-to-Date</h3>
                    <div className="grid grid-cols-3 gap-2 text-sm">
                      <div className="p-2 bg-[var(--background-subtle)] rounded text-center">
                        <p className="font-bold">${viewingStub.ytd_gross?.toFixed(0)}</p>
                        <p className="text-xs text-[var(--text-muted)]">YTD Gross</p>
                      </div>
                      <div className="p-2 bg-[var(--background-subtle)] rounded text-center">
                        <p className="font-bold">${viewingStub.ytd_net?.toFixed(0)}</p>
                        <p className="text-xs text-[var(--text-muted)]">YTD Net</p>
                      </div>
                      <div className="p-2 bg-[var(--background-subtle)] rounded text-center">
                        <p className="font-bold">{viewingStub.ytd_hours?.toFixed(0)}</p>
                        <p className="text-xs text-[var(--text-muted)]">YTD Hours</p>
                      </div>
                    </div>
                  </div>
                )}
                
                {/* Payment Info (if paid) */}
                {viewingStub.status === 'paid' && (
                  <div className="p-3 bg-green-50 border border-green-200 rounded-lg">
                    <p className="font-semibold text-green-800 flex items-center gap-2">
                      <Check className="w-4 h-4" />
                      Payment Completed
                    </p>
                    <p className="text-sm text-green-700 mt-1">
                      Paid via {viewingStub.payment_method?.replace('_', ' ')} on {new Date(viewingStub.paid_at).toLocaleDateString()}
                    </p>
                    {viewingStub.payment_reference && (
                      <p className="text-sm text-green-600">Ref: {viewingStub.payment_reference}</p>
                    )}
                  </div>
                )}
              </div>
              
              <div className="p-4 border-t flex justify-end">
                <Button variant="outline" onClick={() => setViewingStub(null)}>Close</Button>
              </div>
            </motion.div>
          </div>
        )}

        {/* W-2 Generation Modal */}
        {showW2Modal && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              className="bg-[var(--background-paper)] rounded-xl max-w-2xl w-full max-h-[80vh] overflow-hidden"
            >
              <div className="p-6 border-b border-[var(--border-primary)]">
                <h2 className="text-xl font-bold flex items-center gap-2">
                  <FileText className="w-6 h-6 text-[var(--primary)]" />
                  Generate W-2 Forms
                </h2>
                <p className="text-sm text-[var(--text-muted)] mt-1">
                  Generate year-end W-2 tax forms for your employees
                </p>
              </div>
              
              <div className="p-6 overflow-y-auto max-h-[50vh]">
                {/* Year Selection */}
                <div className="mb-6">
                  <label className="block text-sm font-medium mb-2">Tax Year</label>
                  <select
                    value={w2Year}
                    onChange={(e) => setW2Year(parseInt(e.target.value))}
                    className="w-full p-3 border border-[var(--border-primary)] rounded-lg bg-[var(--background-subtle)] text-[var(--text-primary)]"
                  >
                    {[...Array(5)].map((_, i) => {
                      const year = new Date().getFullYear() - i;
                      return <option key={year} value={year}>{year}</option>;
                    })}
                  </select>
                </div>

                {/* Workers List */}
                <div className="space-y-3">
                  <div className="flex items-center justify-between mb-2">
                    <h3 className="font-semibold">Employees ({w2Workers.length})</h3>
                    <Button
                      size="sm"
                      onClick={generateAllW2s}
                      disabled={generatingW2 !== null}
                      className="btn-primary"
                    >
                      Generate All
                    </Button>
                  </div>
                  
                  {w2Workers.length === 0 ? (
                    <p className="text-center py-8 text-[var(--text-muted)]">No workers found</p>
                  ) : (
                    w2Workers.map(worker => (
                      <div
                        key={worker.id}
                        className="flex items-center justify-between p-3 bg-[var(--background-subtle)] rounded-lg"
                      >
                        <div>
                          <p className="font-medium">{worker.full_name}</p>
                          <p className="text-sm text-[var(--text-muted)]">{worker.email}</p>
                        </div>
                        <div className="flex items-center gap-2">
                          {w2Generated.includes(worker.id) ? (
                            <span className="flex items-center gap-1 text-[var(--success)] text-sm">
                              <Check className="w-4 h-4" />
                              Generated
                            </span>
                          ) : (
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => generateW2(worker.id, worker.full_name)}
                              disabled={generatingW2 === worker.id}
                            >
                              {generatingW2 === worker.id ? (
                                <RefreshCw className="w-4 h-4 animate-spin" />
                              ) : (
                                'Generate'
                              )}
                            </Button>
                          )}
                        </div>
                      </div>
                    ))
                  )}
                </div>

                {/* Info Box */}
                <div className="mt-6 p-4 bg-[var(--primary)]/10 rounded-lg border border-[var(--primary)]/30">
                  <div className="flex gap-3">
                    <AlertTriangle className="w-5 h-5 text-[var(--primary)] flex-shrink-0" />
                    <div className="text-sm">
                      <p className="font-medium text-[var(--primary)]">W-2 Information</p>
                      <ul className="mt-1 space-y-1 text-[var(--text-muted)]">
                        <li>• W-2 forms must be provided to employees by January 31st</li>
                        <li>• Forms are generated based on pay stub data for the selected year</li>
                        <li>• Workers can view and download their W-2 from their Tax Forms tab</li>
                      </ul>
                    </div>
                  </div>
                </div>
              </div>
              
              <div className="p-4 border-t border-[var(--border-primary)] flex justify-end">
                <Button variant="outline" onClick={() => setShowW2Modal(false)}>Close</Button>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </motion.div>
  );
};

export default PayrollTab;
